import { Application } from 'pixi.js';
import 'pixi.js/unsafe-eval'; // This poorly-named package enables PixiJS to work in environments with CSP restrictions (it eliminates use of eval())
import { HudContext } from './HudContext';
import {
    BaseWidgetRenderer,
    WidgetPosition,
} from 'ff/debrief/components/hud/widgets/BaseWidgetRenderer';
import { CurrentPoint } from 'ff/debrief/models';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';
import { HudGlobalConfig, DEFAULT_GLOBAL_CONFIG } from './HudGlobalConfig';

/**
 * Main orchestrator for the HUD system.
 *
 * Manages the PixiJS application lifecycle, coordinates widget initialization and updates,
 * handles percentage-to-pixel conversion for positioning, and maintains shared context.
 *
 * @example
 * ```typescript
 * // Create renderer with global visual config
 * const renderer = new HudRenderer(
 *   containerRef.current,
 *   ['latitude', 'longitude', 'altitudeFeet'],
 *   {
 *     fontFamily: 'Roboto Mono',
 *     baseFontSize: 16,
 *     primaryLineColor: 0x00ff00
 *   }
 * );
 *
 * // Initialize PixiJS
 * await renderer.init({
 *   backgroundColor: 0x000000,
 *   antialias: true
 * });
 *
 * // Add widgets with percentage-based positioning
 * await renderer.addWidget(
 *   new LatLonRenderer(),
 *   { x: 0.0, y: 0.0, width: 0.5, height: 0.5 }
 * );
 *
 * // Update on data changes
 * renderer.update(currentPoint);
 *
 * // Cleanup
 * await renderer.destroy();
 * ```
 */
class HudRenderer {
    /** PixiJS application instance managing the canvas and rendering */
    protected pixiApp: Application;

    /** DOM element containing the HUD canvas */
    protected containingDomElement: HTMLDivElement;

    /** Shared context passed to all widgets containing config and utilities */
    protected hudContext: HudContext;

    /** Array of initialized widget instances */
    protected hudElements: BaseWidgetRenderer<unknown>[] = [];

    /** Position translator for HUD-level coordinate conversions */
    protected positionTranslator: HudPositionTranslator;

    /** Set of data properties available from the current data source */
    private readonly availableProperties: Set<keyof CurrentPoint>;

    /** Global visual configuration merged with defaults */
    private readonly globalConfig: HudGlobalConfig;

    /** Factory function for creating PixiJS Application instances (enables testing) */
    private readonly pixiAppFactory: () => Application;

    /**
     * Create a new HUD renderer.
     *
     * @param container - DOM element to contain the HUD canvas (must be mounted in DOM)
     * @param availableProperties - Array of data properties available from the data source
     * @param globalConfig - Optional global visual configuration (merged with defaults)
     * @param pixiAppFactory - Optional factory function for creating Application instances (for testing)
     *
     * @example
     * ```typescript
     * const renderer = new HudRenderer(
     *   document.getElementById('hud-container'),
     *   ['latitude', 'longitude', 'altitudeFeet'],
     *   {
     *     fontFamily: 'Arial',
     *     baseFontSize: 14,
     *     primaryLineColor: 0xffffff
     *   }
     * );
     * ```
     */
    constructor(
        container: HTMLDivElement,
        availableProperties: (keyof CurrentPoint)[],
        globalConfig?: Partial<HudGlobalConfig>,
        pixiAppFactory: () => Application = () => new Application()
    ) {
        this.containingDomElement = container;
        this.availableProperties = new Set(availableProperties);
        this.globalConfig = {
            ...DEFAULT_GLOBAL_CONFIG,
            ...globalConfig,
        };
        this.pixiAppFactory = pixiAppFactory;
    }

    /**
     * Initialize the PixiJS application and HUD context.
     *
     * Must be called before adding widgets. Creates the canvas, appends it to the container,
     * and initializes the shared HudContext with dimensions and configuration.
     *
     * @param options - PixiJS initialization options
     * @param options.backgroundColor - Background color in hex format (e.g., 0x000000)
     * @param options.antialias - Enable antialiasing for smoother rendering (optional, default: false)
     * @returns Promise that resolves when initialization is complete
     *
     * @throws Error if container element is not mounted in DOM
     *
     * @example
     * ```typescript
     * await renderer.init({
     *   backgroundColor: 0x000000,
     *   antialias: true
     * });
     * ```
     */
    public async init(options: {
        backgroundColor: number;
        antialias?: boolean;
    }) {
        this.pixiApp = this.pixiAppFactory();
        await this.pixiApp.init({
            ...options,
            resizeTo: this.containingDomElement,
        });
        this.containingDomElement.appendChild(this.pixiApp.canvas);

        // Initialize HudContext
        const dimensions = {
            width: this.pixiApp.canvas.width,
            height: this.pixiApp.canvas.height,
        };
        this.positionTranslator = new HudPositionTranslator(dimensions);
        this.hudContext = {
            positionTranslator: this.positionTranslator,
            availableProperties: this.availableProperties,
            dimensions,
            globalConfig: this.globalConfig,
        };
    }

    /**
     * Add a widget to the HUD at the specified position.
     *
     * Must be called after init() when canvas dimensions are available.
     * Converts percentage-based positions (0-1) to pixels, checks if widget should display
     * based on available data, and initializes the widget with optional type-safe configuration.
     *
     * @template TConfig - Type of widget-specific configuration
     * @param widget - Widget instance to add
     * @param positionConfig - Position and size in percentages (0-1)
     * @param positionConfig.x - Horizontal position (0 = left, 1 = right)
     * @param positionConfig.y - Vertical position (0 = top, 1 = bottom)
     * @param positionConfig.width - Width as percentage of HUD width (0-1)
     * @param positionConfig.height - Height as percentage of HUD height (0-1)
     * @param positionConfig.anchor - Optional anchor point for positioning (0-1, default: top-left)
     * @param widgetConfig - Optional widget-specific configuration (type-checked)
     * @returns Promise that resolves when widget is initialized
     *
     * @throws Error if called before init()
     *
     * @example
     * ```typescript
     * // Widget without config
     * await renderer.addWidget(
     *   new LatLonRenderer(),
     *   { x: 0.0, y: 0.0, width: 0.5, height: 0.5 }
     * );
     *
     * // Widget with type-safe config
     * await renderer.addWidget(
     *   new AltitudeRenderer(),
     *   { x: 0.9, y: 0.5, width: 0.15, height: 0.3 },
     *   { minAltitude: 0, maxAltitude: 50000, showAGL: true }
     * );
     *
     * // Widget with anchor point (centered)
     * await renderer.addWidget(
     *   new CompassRenderer(),
     *   { x: 0.5, y: 0.5, width: 0.2, height: 0.2, anchor: { x: 0.5, y: 0.5 } }
     * );
     * ```
     */
    public async addWidget<TConfig = Record<string, never>>(
        widget: BaseWidgetRenderer<TConfig>,
        positionConfig: {
            x: number;
            y: number;
            height: number;
            width: number;
            anchor?: { x: number; y: number };
        },
        widgetConfig?: TConfig
    ): Promise<void> {
        if (!this.pixiApp || !this.hudContext) {
            throw new Error('addWidget() must be called after init()');
        }

        // Only add widget if it will display
        const widgetClass = widget.constructor as typeof BaseWidgetRenderer;
        if (!widgetClass.willDisplay(this.availableProperties)) {
            return;
        }

        // Convert percentage values (0-1) to pixels
        const position: WidgetPosition = {
            x: positionConfig.x * this.hudContext.dimensions.width,
            y: positionConfig.y * this.hudContext.dimensions.height,
            width: positionConfig.width * this.hudContext.dimensions.width,
            height: positionConfig.height * this.hudContext.dimensions.height,
            anchor: positionConfig.anchor,
        };

        this.hudElements.push(widget);
        await widget.init(
            this.pixiApp,
            this.hudContext,
            position,
            widgetConfig
        );
    }

    /**
     * Get the set of available data properties.
     *
     * Useful for determining which widgets can be displayed before initialization.
     * Can be used with BaseWidgetRenderer.willDisplay() to check if a widget would display.
     *
     * @returns Set of available CurrentPoint property names
     *
     * @example
     * ```typescript
     * const availableProps = renderer.getAvailableProperties();
     *
     * // Check if a widget would display before creating it
     * if (AltitudeRenderer.willDisplay(availableProps)) {
     *   await renderer.addWidget(new AltitudeRenderer(), ...);
     * }
     * ```
     */
    public getAvailableProperties(): Set<keyof CurrentPoint> {
        return this.availableProperties;
    }

    /**
     * Destroy the HUD and clean up all resources.
     *
     * Destroys all widgets, the PixiJS application, and removes the canvas from the DOM.
     * Should be called when the HUD is no longer needed (e.g., component unmount in React).
     *
     * @returns Promise that resolves when cleanup is complete
     *
     * @example
     * ```typescript
     * // React cleanup
     * useEffect(() => {
     *   const renderer = new HudRenderer(...);
     *   // ... init and add widgets
     *   return () => {
     *     renderer.destroy().catch(console.error);
     *   };
     * }, []);
     * ```
     */
    public async destroy() {
        for (const hudElement of this.hudElements) {
            await hudElement.destroy();
        }
        this.pixiApp.destroy();
    }

    /**
     * Update all widgets with new flight data.
     *
     * Should be called whenever new flight data is available (typically on every data update).
     * Distributes the current point to all initialized widgets.
     *
     * @param currentPoint - Current flight data point containing all available properties
     *
     * @example
     * ```typescript
     * // React effect for data updates
     * useEffect(() => {
     *   if (hudRenderer && currentPoint) {
     *     hudRenderer.update(currentPoint);
     *   }
     * }, [currentPoint, hudRenderer]);
     * ```
     */
    public update(currentPoint: CurrentPoint) {
        this.hudElements.forEach(element =>
            element.update(currentPoint, this.hudContext)
        );
    }
}

export default HudRenderer;
